/**
 * Popover — minimal anchored panel for the canvas inspectors. No positioning
 * library: renders absolutely inside the nearest `relative` ancestor (the
 * canvas region wraps selections in one). Closes on Escape / outside
 * mousedown. Focus lands on the panel for keyboard users.
 */
import { useEffect, useRef } from 'react';

export interface PopoverProps {
  label: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Extra classes for placement tweaks (default: below-left of the anchor). */
  className?: string;
}

export function Popover({ label, onClose, children, className = '' }: PopoverProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  // Every real caller wires `onClose={() => setSelected(null)}` inline, so its
  // identity changes on EVERY host re-render — including the re-render each
  // keystroke in a hosted <input> (FieldInspector's Felirat/Érték, the
  // Colors/Barcode editors' fields, …) triggers. Keeping this effect keyed on
  // `[onClose]` would re-focus the dialog wrapper on every such re-render,
  // stealing focus from whatever the operator is typing into after one
  // keystroke. Register once on mount instead, and read the LATEST onClose
  // via a ref so Escape/outside-click still call the current handler.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    // Remember what had focus before the popover opened, to hand it back on
    // close (guarded — the anchor may have unmounted by then, e.g. a deleted
    // field's button).
    const prev = document.activeElement;
    // Self-focus for keyboard users — but NOT while the operator is already
    // typing: the canvas's inline value <input> mounts (autoFocused) in the
    // same commit as the FieldInspector popover, and stealing its focus would
    // break the spec's click→type-in-one-motion inline editing.
    const typing =
      prev instanceof HTMLElement && (prev.tagName === 'INPUT' || prev.tagName === 'TEXTAREA');
    if (!typing) ref.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      // Restore focus on close so a keyboard flow lands back on its anchor.
      if (prev instanceof HTMLElement && prev.isConnected) prev.focus();
    };
    // Mount-once by design (see comment above) — do NOT add `onClose` here.
  }, []);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={label}
      tabIndex={-1}
      className={`absolute z-20 mt-2 w-72 rounded-lg bg-white p-3 shadow-xl ring-1 ring-slate-200 text-slate-900 ${className}`}
    >
      {children}
    </div>
  );
}
